import { useEffect, useRef, useState } from "react"
import { Box, HStack, Separator, Stack } from "@chakra-ui/react"
import { DealListItem, Section, Text } from "@/components"
import { Deal, DealStep, OfferStatus } from "@/types"

const PAGE_SIZE = 50

const formatEnumLabel = (value: string): string =>
  value
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")

interface Props {
  isLoading: boolean
  deals: Deal[]
}

export const DealsTabComponent = ({ isLoading, deals }: Props) => {

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const [stageFilter, setStageFilter] = useState("all")
  const [stepFilter, setStepFilter] = useState("all")
  const [statusFilter, setStatusFilter] = useState("all")

  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  const filteredDeals = deals
    .filter(d => stageFilter === "all" || d.stage === stageFilter)
    .filter(d => stepFilter === "all" || d.currentStep === stepFilter)
    .filter(d => statusFilter === "all" || (statusFilter === "active" ? d.isActive === 1 : d.isActive === 0))

  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [stageFilter, stepFilter, statusFilter])

  useEffect(() => {
    if (!loadMoreRef.current || !scrollRef.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleCount((prev) =>
            Math.min(prev + PAGE_SIZE, filteredDeals.length)
          )
        }
      },
      {
        root: scrollRef.current,
        threshold: 1
      }
    )

    observer.observe(loadMoreRef.current)

    return () => observer.disconnect()
  }, [filteredDeals.length])

  const visibleDeals = filteredDeals.slice(0, visibleCount)

  return (
    <Section>
      <Text fontSize="xl" fontWeight="bold">
        Your Deals
      </Text>
      <Separator />
      <HStack mt={3} gap={{ base: 2, md: 3 }} px={{ base: 2, md: 10 }} flexWrap="wrap">
        <select
          value={stageFilter}
          onChange={e => setStageFilter(e.target.value)}
          style={{ padding: "6px 10px", borderRadius: "6px", border: "1px solid #E2E8F0", fontSize: "14px" }}
        >
          <option value="all">All Stages</option>
          {Object.values(OfferStatus).map(stage => (
            <option key={stage} value={stage}>{formatEnumLabel(stage)}</option>
          ))}
        </select>
        <select
          value={stepFilter}
          onChange={e => setStepFilter(e.target.value)}
          style={{ padding: "6px 10px", borderRadius: "6px", border: "1px solid #E2E8F0", fontSize: "14px" }}
        >
          <option value="all">All Steps</option>
          {Object.values(DealStep).map(step => (
            <option key={step} value={step}>{formatEnumLabel(step)}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: "6px 10px", borderRadius: "6px", border: "1px solid #E2E8F0", fontSize: "14px" }}
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        {(stageFilter !== "all" || stepFilter !== "all" || statusFilter !== "all") && (
          <Text fontSize="sm" color="gray.500">{filteredDeals.length} deal{filteredDeals.length !== 1 ? "s" : ""}</Text>
        )}
      </HStack>
      <Stack
        ref={scrollRef}
        mt={5}
        pt={5}
        px={{ base: 2, md: 10 }}
        overflowY="auto"
        flex="1"
        minH={0}
      >
        {visibleDeals.map((deal: Deal) => (
          <DealListItem key={deal.id} deal={deal} />
        ))}

        {visibleCount < filteredDeals.length && (
          <Box ref={loadMoreRef} height="40px" />
        )}
      </Stack>
    </Section>
  )
}
